import { inject } from '@angular/core';

import { BrowserService } from '@ideals/services/browser';
import { ErrorLogger, HttpError, LogLevel } from '@ideals/services/error-logger';
import { LOCATION, NAVIGATOR } from '@ideals/types';

import { EmailStorageService } from '../email-storage';

import { LocalErrorLogsService } from './local-error-logs.service';

export interface ErrorLogBase {
  readonly application: 'auth-frontend';
  readonly browser: string;
  readonly clientType: string;
  readonly count: number;
  readonly date: string;
  readonly lastOccurred: string;
  readonly os: string;
  readonly userEmail: string;
}

export interface HttpErrorLog extends ErrorLogBase {
  readonly detail: string;
  readonly error: unknown;
  readonly headers: readonly string[];
  readonly kind: 'http';
  readonly method: string;
  readonly statusCode: number;
  readonly statusText: string;
  readonly url: string;
}

export interface JsErrorLog extends ErrorLogBase {
  readonly exception: string;
  readonly kind: 'js';
  readonly level: LogLevel;
  readonly message: string;
  readonly url: string;
}

export type ErrorLog = HttpErrorLog | JsErrorLog;

export class LocalErrorLogger implements ErrorLogger {
  readonly #browserService = inject(BrowserService);
  readonly #emailStorageService = inject(EmailStorageService);
  readonly #localErrorLogsService = inject(LocalErrorLogsService);
  readonly #location = inject(LOCATION);
  readonly #navigator = inject(NAVIGATOR);

  available(): boolean {
    return this.#localErrorLogsService.available() && !this.#browserService.isMobile;
  }

  async logHttpError(error: HttpError): Promise<void> {
    const key = this.#getHttpErrorKey(error);
    const log = this.#createHttpLog(error);

    await this.#localErrorLogsService.saveLog(key, log);
  }

  async logJsError(error: Error, exception: string, level: LogLevel): Promise<void> {
    const key = this.#getJsErrorKey(error.message, exception);
    const log = this.#createJsLog(error, exception, level);

    await this.#localErrorLogsService.saveLog(key, log);
  }

  #createBaseLog(): ErrorLogBase {
    const deviceInfo = this.#browserService.deviceInfo;
    const platformType = this.#browserService.platformType;

    return {
      application: 'auth-frontend',
      browser: `${deviceInfo.browser} ${deviceInfo.browser_version}`,
      clientType: platformType,
      count: 1,
      date: new Date().toISOString(),
      lastOccurred: new Date().toISOString(),
      os: `${deviceInfo.os} ${deviceInfo.os_version}`,
      userEmail: this.#emailStorageService.get(),
    };
  }

  #createHttpLog(error: HttpError): HttpErrorLog {
    const baseLog = this.#createBaseLog();

    return {
      ...baseLog,
      detail: this.#getHttpErrorDetail(error),
      error: error.error,
      headers: error.headers.keys(),
      kind: 'http',
      method: error.method,
      statusCode: error.statusCode,
      statusText: error.statusText,
      url: error.url ?? '',
    };
  }

  #createJsLog(error: Error, exception: string, level: LogLevel): JsErrorLog {
    const baseLog = this.#createBaseLog();

    return {
      ...baseLog,
      exception,
      kind: 'js',
      level,
      message: error.message,
      url: this.#location.href,
    };
  }

  #getHttpErrorDetail(error: HttpError): string {
    if (error.statusCode === 0) {
      return this.#navigator.onLine
        ? 'Network/server error: request not completed (possible CORS/Firewall/DNS/Timeout)'
        : 'No internet connection';
    }

    return error.message;
  }

  #getHttpErrorKey(error: HttpError): string {
    const pathname = (error.url ?? '').split('?')[0] ?? '';

    return ['http', error.method, pathname, error.statusCode].join(':');
  }

  #getJsErrorKey(message: string, exception: string): string {
    return ['js', message, exception].join(':');
  }
}
